import { useMemo } from 'react';
import { useProjectAngles } from '@/shared/api/hooks/use-project-angles';
import { useSubscriptionLimits } from '@/features/subscription';
import {
  isProjectAngleValid,
  isProjectAngleValidating,
  type ProjectAngle,
} from '@/types/project-angle';

interface UseAnglesOverviewResult {
  angles: ProjectAngle[];
  validAngles: ProjectAngle[];
  isLoading: boolean;
  error: Error | null;
  angleCount: number;
  maxAngles: number;
  /** Per-project library limit (per-concept × multiplier; computed by API). */
  maxLibraryAngles: number;
  canAddMore: boolean;
  hasPendingAngles: boolean;
  hasValidAngles: boolean;
  mainAngle: ProjectAngle | null;
  refetch: () => Promise<unknown>;
}

export function useAnglesOverview(projectId: string): UseAnglesOverviewResult {
  const {
    data: angles = [],
    isLoading,
    error,
    refetch,
  } = useProjectAngles(projectId);

  const limits = useSubscriptionLimits();

  // Per-concept angle limit (used by concept editor — kept here for back-compat).
  const maxAngles = Math.max(1, (limits?.angles as number) ?? 1);

  // Per-project library limit. Falls back to maxAngles × 3 if API hasn't reported it
  // (matches backend Plan::PROJECT_LIBRARY_MULTIPLIER). The page is gated by this limit.
  const maxLibraryAngles = Math.max(
    1,
    (limits?.project_angles as number | undefined) ?? maxAngles * 3
  );

  const computedValues = useMemo(() => {
    const validAngles = angles.filter(isProjectAngleValid);
    const hasPendingAngles = angles.some(isProjectAngleValidating);
    const hasValidAngles = validAngles.length > 0;
    const mainAngle = angles.find((angle) => angle.attributes.is_main) ?? null;

    return {
      validAngles,
      hasPendingAngles,
      hasValidAngles,
      mainAngle,
    };
  }, [angles]);

  return {
    angles,
    validAngles: computedValues.validAngles,
    isLoading,
    error: error as Error | null,
    angleCount: angles.length,
    maxAngles,
    maxLibraryAngles,
    // The angles overview page is for the project library, so capacity is gated
    // by the per-project library limit, not the per-concept limit.
    canAddMore: angles.length < maxLibraryAngles,
    hasPendingAngles: computedValues.hasPendingAngles,
    hasValidAngles: computedValues.hasValidAngles,
    mainAngle: computedValues.mainAngle,
    refetch,
  };
}
